import type { Currency } from '@/api/common';
import { Country } from '@/helpers/conf';
import { License } from '@/store/commonStore/types';

export enum CurrencyCode {
  // FIAT
  EUR = 'EUR',
  USD = 'USD',
  CAD = 'CAD',
  AUD = 'AUD',
  NOK = 'NOK',
  RUB = 'RUB',
  PLN = 'PLN',
  NZD = 'NZD',
  JPY = 'JPY',
  IDR = 'IDR',
  INR = 'INR',
  KRW = 'KRW',
  MYR = 'MYR',
  PHP = 'PHP',
  THB = 'THB',
  VND = 'VND',
  ZAR = 'ZAR',
  // CRYPTO
  BTC = 'BTC',
  MBTC = 'mBTC',
  BCH = 'BCH',
  ETH = 'ETH',
  LTC = 'LTC',
  DOG = 'DOG',
  USDT = 'USDT',
  USDC = 'USDC',
  TRX = 'TRX',
  SOL = 'SOL',
  XRP = 'XRP',
}

export const mapLocaleCurrencyCode: { [locale: string]: string } = {
  en: CurrencyCode.USD,
  de: CurrencyCode.EUR,
  'en-CA': CurrencyCode.CAD,
  'fr-CA': CurrencyCode.CAD,
  'fr-CH': CurrencyCode.EUR,
  it: CurrencyCode.EUR,
  nn: CurrencyCode.NOK,
  no: CurrencyCode.NOK,
  pt: CurrencyCode.EUR,
  'en-AU': CurrencyCode.AUD,
  'en-IE': CurrencyCode.EUR,
  'en-NZ': CurrencyCode.NZD,
  sl: CurrencyCode.EUR,
  'et-EE': CurrencyCode.EUR,
};

export const currencyFlags: {
  [code: string]: string;
} = {
  [CurrencyCode.EUR]: '/currency/EUR.svg',
  [CurrencyCode.USD]: '/currency/USD.svg',
  [CurrencyCode.CAD]: '/currency/CAD.svg',
  [CurrencyCode.AUD]: '/currency/AUD.svg',
  [CurrencyCode.NOK]: '/currency/NOK.svg',
  [CurrencyCode.RUB]: '/currency/RUB.svg',
  [CurrencyCode.PLN]: '/currency/PLN.svg',
  [CurrencyCode.NZD]: '/currency/NZD.svg',
  [CurrencyCode.JPY]: '/currency/JPY.svg',
  [CurrencyCode.BTC]: '/currency/BTC.svg',
  [CurrencyCode.BCH]: '/currency/BCH.svg',
  [CurrencyCode.ETH]: '/currency/ETH.svg',
  [CurrencyCode.LTC]: '/currency/LTC.svg',
  [CurrencyCode.DOG]: '/currency/DOG.svg',
  [CurrencyCode.USDT]: '/currency/USDT.svg',
  [CurrencyCode.USDC]: '/currency/USDC.svg',
  [CurrencyCode.IDR]: '/currency/IDR.svg',
  [CurrencyCode.INR]: '/currency/INR.svg',
  [CurrencyCode.KRW]: '/currency/KRW.svg',
  [CurrencyCode.MYR]: '/currency/MYR.svg',
  [CurrencyCode.PHP]: '/currency/PHP.svg',
  [CurrencyCode.THB]: '/currency/THB.svg',
  [CurrencyCode.VND]: '/currency/VND.svg',
  [CurrencyCode.ZAR]: '/currency/ZAR.svg',
  [CurrencyCode.TRX]: '/currency/TRX_v2.svg',
  [CurrencyCode.SOL]: '/currency/SOL_v2.svg',
  [CurrencyCode.XRP]: '/currency/XRP_v2.svg',
};

export const mapCurrencySymbol: { [code: string]: string } = {
  [CurrencyCode.EUR]: '€{}',
  [CurrencyCode.USD]: '${}',
  [CurrencyCode.CAD]: 'C${}',
  [CurrencyCode.AUD]: 'A${}',
  [CurrencyCode.NOK]: '{} kr',
  [CurrencyCode.RUB]: '{}rub',
  [CurrencyCode.PLN]: '{}zl',
  [CurrencyCode.NZD]: 'NZ${}',
  [CurrencyCode.JPY]: '{} JPY',
  [CurrencyCode.ZAR]: 'R{}',
  [CurrencyCode.BTC]: '{} BTC',
  [CurrencyCode.BCH]: '{} BCH',
  [CurrencyCode.ETH]: '{} ETH',
  [CurrencyCode.LTC]: '{} LTC',
  [CurrencyCode.DOG]: '{} DOGE',
  [CurrencyCode.USDT]: '{} USDT',
  [CurrencyCode.MBTC]: '{} mBTC',
  [CurrencyCode.TRX]: '{} TRX',
  [CurrencyCode.SOL]: '{} SOL',
  [CurrencyCode.XRP]: '{} XRP',
};

export const ALL_FIAT = [
  CurrencyCode.EUR,
  CurrencyCode.USD,
  CurrencyCode.CAD,
  CurrencyCode.AUD,
  CurrencyCode.NOK,
  CurrencyCode.RUB,
  CurrencyCode.PLN,
  CurrencyCode.NZD,
  CurrencyCode.JPY,
  CurrencyCode.IDR,
  CurrencyCode.INR,
  CurrencyCode.KRW,
  CurrencyCode.MYR,
  CurrencyCode.PHP,
  CurrencyCode.THB,
  CurrencyCode.VND,
  CurrencyCode.ZAR,
];

export const ALL_CRYPTO = [
  CurrencyCode.BTC,
  CurrencyCode.BCH,
  CurrencyCode.ETH,
  CurrencyCode.LTC,
  CurrencyCode.DOG,
  CurrencyCode.USDT,
  CurrencyCode.TRX,
  CurrencyCode.SOL,
  CurrencyCode.XRP,
];

export const mapCurrencyName: {
  [code: string]: string;
} = {
  // FIAT
  [CurrencyCode.EUR]: 'Euro',
  [CurrencyCode.USD]: 'United States Dollar',
  [CurrencyCode.CAD]: 'Canadian Dollar',
  [CurrencyCode.AUD]: 'Australian Dollar',
  [CurrencyCode.NOK]: 'Norwegian krone',
  [CurrencyCode.RUB]: 'Russian Ruble',
  [CurrencyCode.PLN]: 'Polish Złoty',
  [CurrencyCode.NZD]: 'New Zealand Dollar',
  [CurrencyCode.JPY]: 'Japanese Yen',
  [CurrencyCode.ZAR]: 'South African Rand',
  // CRYPTO
  [CurrencyCode.BTC]: 'Bitcoin',
  [CurrencyCode.BCH]: 'Bitcoin Cash',
  [CurrencyCode.ETH]: 'Ethereum',
  [CurrencyCode.LTC]: 'Litecoin',
  [CurrencyCode.DOG]: 'Dogecoin',
  [CurrencyCode.USDT]: 'Tether',
  [CurrencyCode.TRX]: 'Tron',
  [CurrencyCode.SOL]: 'Solana',
  [CurrencyCode.XRP]: 'Ripple',
};

export const MAP_COUNTRY_TO_CURRENCY: { [country: string]: string } = {
  [Country.Australia]: CurrencyCode.AUD,
  [Country.NewZealand]: CurrencyCode.NZD,
  [Country.Canada]: CurrencyCode.CAD,
  [Country.Norway]: CurrencyCode.NOK,
  [Country.SouthAfrica]: CurrencyCode.ZAR,
  [Country.Chile]: CurrencyCode.BTC,
  [Country.Mexico]: CurrencyCode.BTC,
  [Country.Peru]: CurrencyCode.BTC,
  [Country.Argentina]: CurrencyCode.BTC,
  [Country.Uruguay]: CurrencyCode.BTC,
  [Country.PuertoRico]: CurrencyCode.BTC,
  [Country.Kazakhstan]: CurrencyCode.USD,
  [Country.Kuwait]: CurrencyCode.USD,
  [Country.UnitedArabEmirates]: CurrencyCode.USD,
  [Country.Germany]: CurrencyCode.EUR,
};

export const mapCurrencyRestrictions: { [country: string]: string[] } = {
  [Country.Mexico]: ALL_FIAT,
  [Country.Argentina]: ALL_FIAT,
  [Country.Uruguay]: ALL_FIAT,
  [Country.Chile]: ALL_FIAT,
  [Country.PuertoRico]: ALL_FIAT,
  [Country.Peru]: ALL_FIAT,
  [Country.Estonia]: ALL_CRYPTO,
};

export const MAP_CURRENCY_BY_LICENSE: { [country: string]: string[] } = {
  [License.EE]: ALL_CRYPTO,
};

export const currencyNameMap: Record<string, string> = {
  [CurrencyCode.LTC]: 'mLTC',
  [CurrencyCode.DOG]: 'DOGE',
  [CurrencyCode.BTC]: 'mBTC',
  [CurrencyCode.BCH]: 'mBCH',
  [CurrencyCode.ETH]: 'mETH',
};

export const CurrencyLimit: Record<string, number> = {
  [CurrencyCode.CAD]: 1000,
  [CurrencyCode.AUD]: 1000,
  [CurrencyCode.EUR]: 1000,
  [CurrencyCode.NZD]: 1000,
  [CurrencyCode.USD]: 1000,
  [CurrencyCode.NOK]: 10_000,
  [CurrencyCode.ZAR]: 20_000,
  [CurrencyCode.BTC]: 20,
  [CurrencyCode.BCH]: 2250,
  [CurrencyCode.ETH]: 700,
  [CurrencyCode.LTC]: 6000,
  [CurrencyCode.DOG]: 4000,
  [CurrencyCode.USDT]: 1000,
};

export const getCurrencyName = (currency: string) =>
  currencyNameMap[currency] ?? currency;

export const decimalPlacesMapCrypto: Record<string, number> = {
  [CurrencyCode.BTC]: 4,
  [CurrencyCode.ETH]: 3,
  [CurrencyCode.LTC]: 2,
  [CurrencyCode.DOG]: 2,
  [CurrencyCode.BCH]: 3,
  [CurrencyCode.SOL]: 3,
  [CurrencyCode.TRX]: 2,
  [CurrencyCode.XRP]: 2,
};

export const decimalPlacesMapMicroCrypto: Record<string, number> = {
  [currencyNameMap[CurrencyCode.BTC]]: 4,
  [currencyNameMap[CurrencyCode.ETH]]: 3,
  [currencyNameMap[CurrencyCode.LTC]]: 2,
  [currencyNameMap[CurrencyCode.DOG]]: 2,
  [currencyNameMap[CurrencyCode.BCH]]: 3,
};

export const fixedAmount = (
  amount: number,
  currencyCode: string,
  decimalPlaces?: number
) => {
  const decimalPlacesByCurrency =
    decimalPlacesMapCrypto[currencyCode] ||
    decimalPlacesMapMicroCrypto[currencyCode] ||
    decimalPlaces;

  return amount ? amount.toFixed(decimalPlacesByCurrency) : '0';
};

interface AmountWithCurrencySymbolOptions {
  wholeRounding?: boolean;
  noDecimals?: boolean;
  spaced?: boolean;
}

export const formatAmountValue = (
  amount: number | string,
  currencyCode: string,
  wholeRounding?: boolean,
  noDecimals?: boolean
): number | string => {
  const hasComma = typeof amount === 'string' && amount.includes(',');
  const isWhole = wholeRounding && Number.isInteger(+amount);
  const amountDecimalPlaces = isWhole ? 0 : 2;

  return hasComma || noDecimals
    ? amount
    : fixedAmount(Number(amount), currencyCode, amountDecimalPlaces);
};

export const amountWithCurrencySymbol = (
  currencyCode: string | undefined,
  amount: number | string,
  options?: AmountWithCurrencySymbolOptions
) => {
  if (currencyCode == null) return amount.toString();

  const mask = mapCurrencySymbol[currencyCode] || null;
  const formattedAmount = formatAmountValue(
    amount,
    currencyCode,
    options?.wholeRounding,
    options?.noDecimals
  );

  if (!mask) return `${formattedAmount} ${currencyCode}`;

  const spacedMask = options?.spaced
    ? mask
        .split('{}')
        .map((part) => part.trim())
        .join(' {} ')
        .trim()
    : mask;

  const result = spacedMask.replace('{}', String(formattedAmount));
  return result;
};

export const amountWithCurrencyCode = (
  currencyCode: string | undefined,
  amount: number | string,
  wholeRounding?: boolean
) => {
  if (currencyCode == null) return amount.toString();

  const currency = DEFAULT_CURRENCIES.find((c) => c.code === currencyCode);
  const resolvedNoDecimals =
    currency != null
      ? !currency.fiat && currencyCode !== CurrencyCode.USDT
      : false;

  const formattedAmount = formatAmountValue(
    amount,
    currencyCode,
    wholeRounding,
    resolvedNoDecimals
  );

  return `${formattedAmount} ${currencyCode}`;
};

export const getCurrencyCodeByLocation = (countryCode?: string) => {
  if (countryCode) {
    return MAP_COUNTRY_TO_CURRENCY[countryCode] || CurrencyCode.EUR;
  }
  return CurrencyCode.AUD;
};

export const getCurrenciesByCountry = (
  currencies: Currency[],
  countryCode: string
) => {
  const unavailableCurrencies = mapCurrencyRestrictions[countryCode] || [];
  return currencies.filter(({ code }) => !unavailableCurrencies.includes(code));
};

export const getCurrenciesByLicense = (
  currencies: Currency[],
  license: string
) => {
  const unavailableCurrencies = MAP_CURRENCY_BY_LICENSE[license] || [];
  return currencies.filter(({ code }) => !unavailableCurrencies.includes(code));
};

export const isCryptoCurrency = (
  currencies: Currency[],
  currencyCode: string
) =>
  currencies?.some(
    (currency) => currency.code === currencyCode && !currency.fiat
  );

export const DEFAULT_CURRENCIES: Currency[] = [
  {
    code: CurrencyCode.AUD,
    symbol: 'A$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.EUR,
    symbol: '€',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.USD,
    symbol: '$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.BTC,
    symbol: 'BTC',
    subunitsToUnit: 100_000_000,
    fiat: false,
    subcurrencies: [
      {
        code: CurrencyCode.MBTC,
        symbol: 'mBTC',
        subunitsToUnit: 100_000,
      },
      {
        code: 'μBTC',
        symbol: 'μBTC',
        subunitsToUnit: 100,
      },
    ],
  },
  {
    code: CurrencyCode.NZD,
    symbol: 'NZ$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.CAD,
    symbol: 'C$',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.ETH,
    symbol: 'ETH',
    subunitsToUnit: 1_000_000_000,
    fiat: false,
    subcurrencies: [
      {
        code: 'mETH',
        symbol: 'mETH',
        subunitsToUnit: 1_000_000,
      },
      {
        code: 'μETH',
        symbol: 'μETH',
        subunitsToUnit: 1000,
      },
    ],
  },
  {
    code: CurrencyCode.USDT,
    symbol: 'USDT',
    subunitsToUnit: 100_000_000,
    fiat: false,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.NOK,
    symbol: 'kr',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.LTC,
    symbol: 'LTC',
    subunitsToUnit: 100_000_000,
    fiat: false,
    subcurrencies: [
      {
        code: 'mLTC',
        symbol: 'mLTC',
        subunitsToUnit: 100_000,
      },
      {
        code: 'μLTC',
        symbol: 'μLTC',
        subunitsToUnit: 100,
      },
    ],
  },
  {
    code: CurrencyCode.DOG,
    symbol: 'DOGE',
    subunitsToUnit: 100_000_000,
    fiat: false,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.BCH,
    symbol: 'BCH',
    subunitsToUnit: 100_000_000,
    fiat: false,
    subcurrencies: [
      {
        code: 'mBCH',
        symbol: 'mBCH',
        subunitsToUnit: 100_000,
      },
      {
        code: 'μBCH',
        symbol: 'μBCH',
        subunitsToUnit: 100,
      },
    ],
  },
  {
    code: CurrencyCode.ZAR,
    symbol: 'R',
    subunitsToUnit: 100,
    fiat: true,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.TRX,
    symbol: 'TRX',
    subunitsToUnit: 1_000_000,
    fiat: false,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.SOL,
    symbol: 'SOL',
    subunitsToUnit: 1_000_000_000,
    fiat: false,
    subcurrencies: [],
  },
  {
    code: CurrencyCode.XRP,
    symbol: 'XRP',
    subunitsToUnit: 1_000_000,
    fiat: false,
    subcurrencies: [],
  },
];
